import { Alert, Snackbar } from '@mui/material';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const MAX_SNACKS = 3; // Maximum number of simultaneous notifications

interface SnackbarMessage {
  id: number;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
  autoHide: boolean;
}

type SnackbarContextType = {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
};

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

type SnackbarProviderProps = {
  children: ReactNode;
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [snacks, setSnacks] = useState<SnackbarMessage[]>([]);

  const handleClose = useCallback((id: number) => {
    setSnacks((prev) => prev.filter((snack) => snack.id !== id));
  }, []);

  const showMessage = useCallback(
    (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
      const newSnack: SnackbarMessage = {
        id: Date.now(),
        message,
        severity,
        autoHide: severity !== 'error',
      };

      setSnacks((prev) => {
        if (prev.length >= MAX_SNACKS) {
          const nonErrorIndex = prev.findIndex(
            (snack) => snack.severity !== 'error'
          );
          const removeIndex = nonErrorIndex >= 0 ? nonErrorIndex : 0;
          return [
            ...prev.slice(0, removeIndex),
            ...prev.slice(removeIndex + 1),
            newSnack,
          ];
        }
        return [...prev, newSnack];
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      showError: (message: string) => showMessage(message, 'error'),
      showSuccess: (message: string) => showMessage(message, 'success'),
      showInfo: (message: string) => showMessage(message, 'info'),
      showWarning: (message: string) => showMessage(message, 'warning'),
    }),
    [showMessage]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000 }}>
        {snacks.map((snack, index) => (
          <Snackbar
            key={snack.id}
            open={true}
            autoHideDuration={snack.autoHide ? 6000 : null}
            onClose={() => handleClose(snack.id)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              position: 'relative !important',
              top: 'auto !important',
              right: 'auto !important',
              mb: index < snacks.length - 1 ? 2 : 0,
            }}
          >
            <Alert
              onClose={() => handleClose(snack.id)}
              severity={snack.severity}
              sx={{ width: '100%' }}
              elevation={6}
            >
              {snack.message}
            </Alert>
          </Snackbar>
        ))}
      </div>
    </SnackbarContext.Provider>
  );
};
